import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUACHelper } from '../../contexts/UACContext';
import UACConstants from '../../constants/UACConstants';
import { cloneDeep } from "../../utils/loadash.utils";
import { currentDate, startOfDay } from "../../utils/dateUtils";
//common components
import { AppState } from "../../config/redux/reducers";
import AppConstants from "../../constants";
import GridWrapper from "../../common/GridWrapper";
import Loader from "../../common/Loader";
import CustomSnackbar from "../../common/CustomSnackbar";
import SelectBox from "../../common/SelectBox";
import DateRangePickerComponent from "../../common/DateRangeComponent/index";
import { TextConstants } from "constants/TextConstants";
//Material UI Components
import { CellParams } from "../../common/GridWrapper/DataTable";
import {
  Grid,
  Typography,
  Button,
  IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
//styles
import { useStyles } from "../shared/codportal.styles";
//assets
import calendarIcon from "../../assets/icons/calendar-icon.svg";
import arrowIcon from "../../assets/icons/CaretCircleRight.svg";
//redux
import { userLogout } from "../../Login/redux/loginSlice";
import { handleOrdersHeaderButtonClick as handleOrdersHeaderButtonClickUtil } from "../CodUtil";

import BucketComponent from "../../common/BucketComponent";
import ExportInitiatedDialog from "ExportIntiaiedDialog";
import useExportInitiated from "CustomHook/ExortInitiatedHook";

export interface CODPortalBaseProps {
  match?: any;
  location?: any;
  // Store data
  reduxStates: {
  loading: boolean;
  error: string | null;
  errorCode: string | null;
  excelLoading: boolean;
  exportError: string | null;
  isAmtCollectedForOrders: boolean;
  exportInitiated: boolean;
  reportId: string;
  driversData: any;
  driversFiltersDropdowns: any;
  };
  // Config
  tableWrapperDataConstant: any;
  // Actions
  actions: {
  fetchDriversList: (payload: any) => void;
  exportToExcel: (payload: any) => void;
  createReceiptDraft: (payload: any) => void;
  resetExportInitiated: () => void;
  };

  transforms: {
  createPayloadToFetchDriversList: (filters: any, params: any, driversData: any, isLoadMore: boolean | undefined, countryCode: string, datePickerValue: any, dashboardHub: string) => any;
  driversDataTransform: (driversData: any) => any[];
  };
  // Components
  DriverDetails: React.ComponentType<any>;
  UploadReceipt: React.ComponentType<any>;
  moduleName?: string;
}

const CODPortalBase = ({
  reduxStates,
  transforms,
  actions,
  tableWrapperDataConstant,
  DriverDetails,
  UploadReceipt,
  moduleName
}: CODPortalBaseProps) => {

  const dispatch = useDispatch();
  const classes = useStyles();

  const {driversData, loading, driversFiltersDropdowns, error, errorCode, excelLoading, exportError, isAmtCollectedForOrders, exportInitiated, reportId} = reduxStates
  const { createPayloadToFetchDriversList, driversDataTransform,} = transforms
  const {
    fetchDriversList,
    exportToExcel,
    createReceiptDraft,
    resetExportInitiated
  } = actions;
  const {
    countryCode,
    hubRecordList,
    hubRecordListWithCodeAndName,
  } = useSelector((state: AppState) => state.common);
  const uacHelper = useUACHelper();
  const canEditExpressCOD = uacHelper.canEdit(UACConstants.ED_COD);
  const canEditStandardCOD = uacHelper.canEdit(UACConstants.SD_COD);

  const country =
    sessionStorage.getItem(AppConstants.COUNTRY_CODE) ||
    countryCode ||
    AppConstants.COUNTRY_OBJ.value;
    
  const [openDriverDetails, setOpenDriverDetails] = useState(false);
  const [dashboardHub, setDashboardHub] = useState(
    hubRecordList[
      sessionStorage.getItem(AppConstants.COUNTRY_CODE) || countryCode
    ]?.[0]?.value ?? ""
  );
  const [datePickerValue, setDatePickerValue] = useState({
    startDate: currentDate(AppConstants.DATE_FORMAT),
    endDate: currentDate(AppConstants.DATE_FORMAT),
  });
  const [driversTableWrapperData, setDriversTableWrapperData] = useState({
    ...tableWrapperDataConstant.DRIVERS_DATA.TABLE_WRAPPER_DATA,
    HeaderData: tableWrapperDataConstant.DRIVERS_DATA.TABLE_WRAPPER_DATA.HeaderData.map(
      (header: any) => {
        const newHeader = header;
        if (newHeader.field === "actions") {
          newHeader.renderCell = (params: CellParams) => (
            <img className="actionsLink" src={arrowIcon} alt="arrow" />
          );
        }
        return newHeader;
      }
    ),
    defaultFiltersObj: {
      ...tableWrapperDataConstant.DRIVERS_DATA.TABLE_WRAPPER_DATA
        .defaultFiltersObj,
      dateRange: {
        ...tableWrapperDataConstant.DRIVERS_DATA.TABLE_WRAPPER_DATA
          .defaultFiltersObj.dateRange,
        value: {
          datePickerValue,
        },
      },
    },
  });
  const [driversActiveFilters, setDriversActiveFilters] = useState(
    driversTableWrapperData.defaultFiltersObj
  );
  const [ordersSearchValue, setOrdersSearchValue] = useState("");
  const [isOrderSearched, setIsOrderSearched] = useState(false);

  const [driversListToShow, setDriversListToShow] = useState([]);
  const [driverDetails, setDriverDetails] = useState([]);

  const [renderExcelProgress, setRenderExcelProgress] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const {
    openExportInitiatedInfo,
    handleExportInitiatedDialogClose,
  } = useExportInitiated(resetExportInitiated, exportInitiated);
  const [openViewAndUploadPopup, setOpenViewAndUploadPopup] = useState(false);
  const snackbarType = useRef(AppConstants.SNACKBAR.TYPES.SUCCESS);
  const snackbarMessage = useRef("");

  const closeDriverDetailsPopup = () => {
    setOpenDriverDetails(false);
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleProgressClose = () => {
    setRenderExcelProgress(false);
  };

  const handleSnackbarExited = useCallback(() => {
    if (
      errorCode ===
      tableWrapperDataConstant.RESPONSE_CONSTANTS.ERROR_CODES
        .SESSION_TIMEOUT
    ) {
      dispatch(userLogout());
    }
  }, [dispatch, errorCode]);

  const driversFilterCallback = useCallback(
    (filters: any, isLoadMore?: boolean, params?: any) => {
      if (filters) {
        const payloadFilters = cloneDeep(filters);
        payloadFilters.pageSize = driversTableWrapperData.loadMore.pageSize;
        const payload = createPayloadToFetchDriversList(
          payloadFilters,
          params,
          driversData,
          isLoadMore,
          countryCode,
          datePickerValue,
          dashboardHub
        );
        dispatch(
          fetchDriversList({
            params: payload,
          })
        );
        setDriversActiveFilters(filters);
      }
      setRenderExcelProgress(false);
    },
    [
      dispatch,
      driversData,
      driversTableWrapperData,
      countryCode,
      dashboardHub,
      datePickerValue,
    ]
  );

  const handleExcelExport = useCallback(
    (
      filters: any,
      hub: any,
      dateValue: any,
      type: string,
      country?: string
    ) => {
      if (filters) {
        const payloadFilters = cloneDeep(filters);
        payloadFilters.pageSize = driversTableWrapperData.loadMore.pageSize;
        const payload = createPayloadToFetchDriversList(
          payloadFilters,
          driversData,
          driversData,
          false,
          country || countryCode,
          dateValue,
          hub
        );
        dispatch(
          exportToExcel({
            payload: payload,
            type: type,
          })
        );
      }
      setRenderExcelProgress(true);
    },
    [dispatch, driversData, country, dashboardHub, datePickerValue]
  );

  const handleOrdersHeaderButtonClick = useCallback(
    (btnObj: any) => {
      handleOrdersHeaderButtonClickUtil(
        btnObj,
        driversActiveFilters,
        dashboardHub,
        datePickerValue,
        handleExcelExport,
        country
      );
    },
    [
      driversActiveFilters,
      dashboardHub,
      datePickerValue,
      country,
      handleExcelExport,
    ]
  );

  const handleViewAndUploadDialogClose = () => {
    setOpenViewAndUploadPopup(false);
  };

  const handleFetchDriverParameters = useCallback(
    (params: any) => {
      driversFilterCallback(driversActiveFilters, false, params);
    },
    [driversActiveFilters, driversFilterCallback]
  );

  const handleViewAndUploadClick = useCallback(
    (hubId: any, date: any) => {
      const payload = {
        hubId: hubId,
        reconcileDate: startOfDay(
          date.startDate,
          AppConstants.DATE_FORMAT,
          AppConstants.DATE_FORMAT_BACKEND
        ),
      };
      dispatch(
        createReceiptDraft({
          params: payload,
        })
      );
      setOpenViewAndUploadPopup(true);
    },
    [dispatch]
  );

  const handleDateChange = (dateRange: any) => {
    setDatePickerValue(dateRange);
    handleFetchDriverParameters({
      dateRange: dateRange,
      hubCode: dashboardHub,
    });
  };

  const handleHubChange = useCallback(
    (value: any) => {
      setDashboardHub(value);
      handleFetchDriverParameters({
        dateRange: datePickerValue,
        hubCode: value,
      });
    },
    [datePickerValue, handleFetchDriverParameters]
  );

  const handleLoadMoreDisableState = useCallback(() => {
    const loadMoreState = driversTableWrapperData.loadMore;
    loadMoreState.disabled = driversData.totalPages
      ? driversData.pageNumber + 1 >= driversData.totalPages
      : true;
    setDriversTableWrapperData({
      ...driversTableWrapperData,
      loadMore: loadMoreState,
    });
  }, [driversTableWrapperData, driversData]);

  const ordersSearchFilterHandler = (
    data: { [key: string]: string },
    updatedSearchValue: string,
    filters: any
  ) => {
    const activeFilters = cloneDeep(filters);
    setOrdersSearchValue(updatedSearchValue);
    setIsOrderSearched(false);
    if (updatedSearchValue) {
      setIsOrderSearched(true);
      for (const searchKey in data) {
        if (data[searchKey]) {
          activeFilters.searchAttribute = {
            key: searchKey,
            value: data[searchKey],
          };
          driversFilterCallback(activeFilters);
          return;
        }
      }
    }
  };

  const openSnackbarPopup = (msg: string, type: string) => {
    snackbarMessage.current = msg;
    snackbarType.current = type;
    setOpenSnackbar(true);
  };

  const handleDriverCellClick = (params: CellParams) => {
    if (params.field === "actions") {
      const selectedRow: any = params.rowData;
      setDriverDetails(selectedRow);
      setOpenDriverDetails(true);
    }
  };

  useEffect(() => {
    if (driversTableWrapperData && driversTableWrapperData.HeaderData) {
      const headerData = driversTableWrapperData.HeaderData.map((header: any) => {
        if (header.filterObj) {
          if (header.filterObj.fieldName === "driverName") {
            header.filterObj.items =
              driversFiltersDropdowns.driverNameList || [];
          }
          if (header.filterObj.fieldName === "vendorName") {
            header.filterObj.items =
              driversFiltersDropdowns.vendorNameList || [];
          }
        }
        return header;
      });
      setDriversTableWrapperData({
        ...driversTableWrapperData,
        HeaderData: headerData,
      });
    }

  }, [driversFiltersDropdowns]);

  useEffect(() => {
    setDriversListToShow(driversDataTransform(driversData) as any);
    handleLoadMoreDisableState();
  }, [driversData]);

  useEffect(() => {
    setDatePickerValue({
      startDate: currentDate(AppConstants.DATE_FORMAT),
      endDate: currentDate(AppConstants.DATE_FORMAT),
    });
  }, [country]);

  useEffect(() => {
    if (error) {
      openSnackbarPopup(error, AppConstants.SNACKBAR.TYPES.ERROR);
    }
  }, [error]);

  useEffect(() => {
    if (isAmtCollectedForOrders) {
      driversFilterCallback(driversActiveFilters);
    }
  }, [isAmtCollectedForOrders]);

  const getHeaderConfig = useMemo(() => {
    const driverCodDetails = moduleName ? driversData?.results : driversData;
    return [
      {
        tileHeading: TextConstants.TOTAL_COD_ORDERS,
        tileData: driverCodDetails?.totalOrders,
        subHeading: `(${country})`,
      },
      {
        tileHeading: TextConstants.TOTAL_ORDERS_AMOUNT,
        tileData: driverCodDetails?.totalOrdersCODAmount,
        subHeading: `(${country})`,
      },
      {
        tileHeading: TextConstants.TOTAL_COD_COLLECTED,
        tileData: driverCodDetails?.totalCODAmount,
        subHeading: "(By Driver)",
      },
      {
        tileHeading: TextConstants.COD_AMOUNT_RECEIVED,
        tileData: driverCodDetails?.totalCODReceived,
        subHeading: "(By Dispatcher)",
      },
      {
        tileHeading: TextConstants.TOTAL_COD_AMOUNT,
        tileData: driverCodDetails?.totalCODPending,
        subHeading: "pending",
        classes: { subHeading: classes.subHeadingBold },
      },
    ];
  }, [driversData, country, classes.subHeadingBold]);

  const HeaderComponent = () => {
    const config = getHeaderConfig;

    return (
      <Grid className={classes.bucketHeader}>
        <BucketComponent config={config} />
      </Grid>
    );
  };
  useEffect(() => {
    setDashboardHub(
      hubRecordList[
        sessionStorage.getItem(AppConstants.COUNTRY_CODE) || countryCode
      ]?.[0]?.value ?? ""
    );
  }, [hubRecordList]);

  return (
    <>
      <div className={classes.root}>
        {loading && <Loader></Loader>}
        <Grid container justify="space-between">
          <Grid item xs={4} className={classes.container}></Grid>
          <Grid item xs={6} className={classes.container}>
            <Grid container spacing={2}>
              <Grid item className={classes.tilesView}>
                <SelectBox
                  fullWidth
                  className={classes.select}
                  handleChange={handleHubChange}
                  value={dashboardHub}
                  label="Hub"
                  enableSearch={true}
                  id="dashboard-hub-select-box"
                  items={
                    hubRecordListWithCodeAndName[
                      sessionStorage.getItem(AppConstants.COUNTRY_CODE) ||
                        countryCode
                    ] || []
                  }
                ></SelectBox>
              </Grid>
              <Grid item className={classes.tilesView}>
                <DateRangePickerComponent
                  fullWidth
                  keyId="create-route-date-picker"
                  label="Date"
                  className={classes.icon}
                  value={datePickerValue}
                  isTextField={true}
                  iconSrc={calendarIcon}
                  autoUpdateInput={false}
                  autoApply={true}
                  maxSpan={{
                    day: "30",
                  }}
                  showDropdowns={true}
                  linkedCalendars={true}
                  locale={{
                    format: AppConstants.DATE_FORMAT,
                  }}
                  updateDateRange={handleDateChange}
                />
              </Grid>
              {(canEditExpressCOD || canEditStandardCOD) && (
                <>
                  {datePickerValue.startDate === datePickerValue.endDate ? (
                    <Grid item>
                      <Button
                        className="dialogBtn primary"
                        variant="contained"
                        onClick={() => {
                          dashboardHub &&
                            handleViewAndUploadClick(
                              dashboardHub,
                              datePickerValue
                            );
                        }}
                      >
                        {AppConstants.BUTTONS.VIEW_AND_UPLOAD}
                      </Button>
                    </Grid>
                  ) : null}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
        <HeaderComponent />

        <Grid item className="panelItem gridWrapper">
          <GridWrapper
            showHeader={true}
            checkboxSelection={false}
            headerData={driversTableWrapperData.HeaderData}
            rowData={driversListToShow}
            loading={loading}
            searchTypes={driversTableWrapperData.SEARCH_TYPES}
            defaultFilter={driversTableWrapperData.defaultFiltersObj}
            activeFilter={driversActiveFilters}
            headerButtons={driversTableWrapperData.headerButtons}
            title={driversTableWrapperData.title}
            disableSelectionOnClick={true}
            searchValue={ordersSearchValue}
            loadMore={{
              ...driversTableWrapperData.loadMore,
              rowCount: driversData.totalElements || 0,
            }}
            filterGrid={driversFilterCallback}
            headerBtnClick={handleOrdersHeaderButtonClick}
            searchCallback={ordersSearchFilterHandler}
            onCellClick={handleDriverCellClick}
          />
        </Grid>
        {!error && renderExcelProgress
          ? excelLoading && (
              <Grid container className={classes.excelDownloadStatus}>
                <Grid item>
                  <Typography className={classes.excelDownloadStatusText}>
                    {AppConstants.EXPORT_FILE_CONSTANT.DOWNLOAD_INITIATED}
                  </Typography>
                </Grid>
                <Loader />
              </Grid>
            )
          : exportError === AppConstants.ERRORS.INTERNAL_SERVER_ERROR &&
            renderExcelProgress && (
              <Grid container className={classes.excelDownloadError}>
                <Grid item>
                  <Typography className={classes.excelDownloadStatusText}>
                    {AppConstants.EXPORT_FILE_CONSTANT.DOWNLOAD_FAIL}
                  </Typography>
                </Grid>
                <Grid item className={classes.container}>
                  <IconButton
                    aria-label="close"
                    className={classes.close}
                    onClick={handleProgressClose}
                  >
                    <CloseIcon className={classes.closeIcon} />
                  </IconButton>
                </Grid>
              </Grid>
            )}
        <DriverDetails
          open={openDriverDetails}
          driverDetails={driverDetails}
          dashboardHub={dashboardHub}
          dateRange={datePickerValue}
          closePopup={closeDriverDetailsPopup}
        />
        <UploadReceipt
          open={openViewAndUploadPopup}
          hub={dashboardHub}
          date={datePickerValue}
          closePopup={handleViewAndUploadDialogClose}
        />
        <CustomSnackbar
          open={openSnackbar}
          handleClose={handleSnackbarClose}
          onExited={handleSnackbarExited}
          autoHideDuration={AppConstants.SNACKBAR.AUTO_HIDE_TIMEOUT}
          message={snackbarMessage.current}
          type={snackbarType.current}
        />
        {openExportInitiatedInfo && (
          <ExportInitiatedDialog
            openExportInitiatedInfo={openExportInitiatedInfo}
            handleDialogClose={handleExportInitiatedDialogClose}
            reportId={reportId}
          />
        )}
      </div>
    </>
  );
};

export default CODPortalBase;